/**
 * Onboarding API client.
 *
 * Talks to the dreambooth studio's onboarding endpoints:
 *   - GET  /api/projects/by-slug?slug=X&checkOnly=true   slug availability
 *   - GET  /api/onboarding/catalog?mode=...              frames + filters (public)
 *   - POST /api/onboarding/generate                      AI booth generation (public, capped)
 *   - POST /api/projects/onboarding                      create (AUTH REQUIRED)
 *   - GET  /api/auth/session                             studio session probe
 *   - GET  /api/ai-effects/catalog                       public AI effects (auth)
 *
 * Base URL is process.env.VITE_API_URL (the studio origin), the same env
 * the rest of the renderer uses for studio calls.
 */

import { getStoredAuth, getStudioToken } from './studioToken'

const API_BASE = (): string => process.env.VITE_API_URL || 'https://dreamboothstudio.com'

/**
 * Authorization header for studio calls. Sends the bearer token captured from
 * the sign-in popup so authed endpoints work even when the studio session
 * cookie is blocked as a third-party cookie (Incognito / ITP). Empty when
 * signed out — public endpoints don't need it, and the same-origin cookie
 * (app.dreamboothstudio.com) still flows via credentials:include.
 */
export function authHeaders(): Record<string, string> {
  const token = getStudioToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export type OnboardingMode = 'creator' | 'product'

export interface CatalogFilter {
  _id: string
  name: string
  owner: string
  adjustments: Record<string, number>
  postLUTAdjustments?: Record<string, number>
  filterMode: 'manual' | 'lut'
  cachedThumbnail: string | null
  lutData?: { size?: number; title?: string; filename?: string }
  /** Overlay layer, when the catalog filter carries one. Applies in both modes. */
  overlay?: {
    url: string
    blendMode?: string
    opacity?: number
    fit?: 'cover' | 'stretch'
  } | null
}

export interface CatalogFrame {
  _id: string
  name: string
  owner: string
  thumbnail: string
  source: string
  drawParams: { canvasWidth: number; canvasHeight: number }
  createdAt?: string
}

export interface OnboardingCatalog {
  mode: OnboardingMode | null
  frames: CatalogFrame[]
  filters: {
    official: CatalogFilter[]
    community: CatalogFilter[]
  }
}

// ---- AI generation --------------------------------------------------------

export interface BoothSpecDto {
  title: string
  slugBase: string
  palette: {
    backgroundColor: string
    primaryColor: string
    secondaryColor: string
    dark: boolean
  }
  welcome: {
    headline: string
    subtext: string
    cta: string
    layoutArchetype: 'centered-hero' | 'bottom-stack' | 'top-brand'
  }
  captureMode: 'standard' | 'frame-based'
  imagePrompts: { welcomeBg: string; appBg: string }
  frameTags: string[]
  filterMood: string
  /** Ready-to-use AI-frame style prompt (may be empty — compose a fallback). */
  framePrompt?: string
  locale: string
}

export interface WelcomeComponentDto {
  type: 'image' | 'text' | 'button'
  top: number
  left: number
  width: number
  height: number
  rotation: number
  src?: string
  alt?: string
  text?: string
  buttonAction?: string
  style: Record<string, unknown>
}

export interface WelcomeDesignDto {
  components: WelcomeComponentDto[]
  orientedComponents: {
    portrait: WelcomeComponentDto[]
    landscape: WelcomeComponentDto[]
  }
}

export interface GenerateResponse {
  /** "designed" = the full designer pass; "archetype" = the simpler fallback. */
  designMode?: 'designed' | 'archetype'
  draftId: string
  slug: string
  spec: BoothSpecDto
  assets: {
    welcomeBgPortrait: string
    welcomeBgLandscape: string
    appBg: string
    logoUrl: string
  }
  welcomeDesign: WelcomeDesignDto | null
  remaining: { fullGenerations: number; regens: number }
}

/** 'welcome' = both orientations; the server routes the hint to one or both. */
export type RegenTarget = 'welcome' | 'welcomeBgPortrait' | 'welcomeBgLandscape' | 'appBg'

export class ApiError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/**
 * Thrown when a request outlives `timeoutMs`. Distinct from ApiError so the
 * caller can say "this is taking too long" rather than "this failed".
 */
export class TimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TimeoutError'
  }
}

async function postJson<T>(path: string, body: unknown, timeoutMs?: number): Promise<T> {
  const controller = timeoutMs ? new AbortController() : null
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null
  let res: Response
  try {
    res = await fetch(`${API_BASE()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: controller?.signal
    })
  } catch (err) {
    if (controller?.signal.aborted) {
      throw new TimeoutError('This is taking longer than expected.')
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string; code?: string }
  if (!res.ok) {
    throw new ApiError(data.error || `Request failed (${res.status})`, res.status, data.code)
  }
  return data
}

/**
 * Ceiling on a full generation. The server does four image calls plus a spec
 * and a QA pass; a slow run has been measured at ~150s, and the serverless
 * function itself is capped at 300s. Without a client-side limit a request
 * that never returns leaves the creator on the generation theater forever,
 * with no error and no way back — which is exactly what happened.
 */
// 5.5 min: the serverless function itself may run 300s (QA retries +
// photo compositing pushed real runs to 3-4 min; Ale hit the old 4-min
// client ceiling while the server was still finishing — the client
// reported failure, the booth completed anyway, and a retry would have
// double-spent a generation). Server cap + network margin, never less.
const GENERATE_TIMEOUT_MS = 5.5 * 60 * 1000

export interface AttachmentInput {
  dataUrl: string
  name?: string
  /** literal = place in the design; style = reference only; auto = model decides. */
  role: 'auto' | 'literal' | 'style'
}

export interface Clarification {
  question: string
  answer: string
}

export async function generateBooth(input: {
  draftId?: string
  prompt: string
  locale: string
  logoDataUrl?: string
  attachments?: AttachmentInput[]
  clarifications?: Clarification[]
}): Promise<GenerateResponse> {
  return postJson<GenerateResponse>('/api/onboarding/generate', input, GENERATE_TIMEOUT_MS)
}

// ---- Clarifying questions ---------------------------------------------------

export interface ClarifyQuestion {
  id: string
  question: string
  type: 'single' | 'multi'
  options: string[]
  allowOther: boolean
}

/**
 * Optional pre-generation step: returns a draftId plus up to 5 quick
 * multiple-choice questions. Fail-open — on any error the caller proceeds
 * straight to generation without questions.
 */
export async function fetchClarifyQuestions(input: {
  prompt: string
  locale: string
}): Promise<{ draftId: string; questions: ClarifyQuestion[] } | null> {
  try {
    return await postJson<{ draftId: string; questions: ClarifyQuestion[] }>(
      '/api/onboarding/clarify',
      input
    )
  } catch {
    return null
  }
}

export async function regenerateAsset(input: {
  draftId: string
  regenTarget: RegenTarget
  hint?: string
}): Promise<GenerateResponse> {
  // Same ceiling as a full generation: a hung regen used to leave
  // regenInFlight set forever, permanently disabling both regen buttons and
  // pinning the preview into its pulse state with no error and no way out —
  // the exact failure mode GENERATE_TIMEOUT_MS already fixed for the main
  // call.
  return postJson<GenerateResponse>('/api/onboarding/generate', input, GENERATE_TIMEOUT_MS)
}

// ---- Session / auth --------------------------------------------------------

export interface StudioSessionUser {
  email: string
  name?: string
  image?: string
}

/**
 * Returns the signed-in studio user, or null. Never throws.
 *
 * Prefers the bearer token captured by the sign-in popup (works cross-origin
 * with third-party cookies blocked). Falls back to the cookie-backed session
 * endpoint for the same-origin case (app.dreamboothstudio.com/new).
 */
export async function fetchStudioSession(): Promise<StudioSessionUser | null> {
  const stored = getStoredAuth()
  if (stored) return stored.user
  try {
    const res = await fetch(`${API_BASE()}/api/auth/session`, {
      credentials: 'include',
      cache: 'no-cache'
    })
    if (!res.ok) return null
    const data = (await res.json()) as {
      user?: { email?: string; name?: string; image?: string }
    }
    if (!data?.user?.email) return null
    return { email: data.user.email, name: data.user.name, image: data.user.image }
  } catch {
    return null
  }
}

/**
 * Studio sign-in URL for the popup flow. After Google OAuth the popup lands on
 * the studio's popup-complete handoff page, which postMessages the session
 * token + user back to this opener and closes — no third-party cookie needed,
 * and no detour through the studio onboarding wizard. `origin` is passed so the
 * handoff page can target its postMessage at exactly this window's origin.
 */
export function studioSignInUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const complete = `${API_BASE()}/auth/popup-complete?origin=${encodeURIComponent(origin)}`
  return `${API_BASE()}/login?callbackUrl=${encodeURIComponent(complete)}`
}

// ---- AI effects (public catalog) --------------------------------------------

export interface CatalogEffect {
  id: string
  title: string
  ownerLabel: string
  previewBeforeUrl?: string
  previewAfterUrl?: string
}

/** Requires a studio session; resolves to null when signed out. */
export async function fetchEffectCatalog(): Promise<CatalogEffect[] | null> {
  try {
    const res = await fetch(`${API_BASE()}/api/ai-effects/catalog`, {
      credentials: 'include',
      cache: 'no-cache',
      headers: authHeaders()
    })
    if (!res.ok) return null
    const data = (await res.json()) as { items?: CatalogEffect[] }
    return data.items ?? []
  } catch {
    return null
  }
}

// ---- Existing endpoints ------------------------------------------------------

/** Returns { available }. Network/parse errors resolve to available:false (safe). */
export async function checkSlugAvailability(slug: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE()}/api/projects/by-slug?slug=${encodeURIComponent(slug)}&checkOnly=true`,
      { credentials: 'include', cache: 'no-cache', headers: authHeaders() }
    )
    if (!res.ok) return false
    const data = (await res.json()) as { available?: boolean }
    return data.available === true
  } catch {
    return false
  }
}

// ---- AI frame generation (blank-template chat flow) -------------------------

export interface BlankTemplate {
  templateId: string
  width: number
  height: number
  sizeClass: string
  placeholderCount: number
  placeholderShapeType: string
  previewUrl: string
  sourceUrl: string
}

export async function fetchBlankTemplates(): Promise<BlankTemplate[]> {
  const res = await fetch(`${API_BASE()}/api/onboarding/blank-templates`, {
    credentials: 'include',
    headers: authHeaders()
  })
  if (!res.ok) throw new Error(`Failed to load templates (${res.status})`)
  const data = (await res.json()) as { templates?: BlankTemplate[] }
  return data.templates ?? []
}

/** Creates a chat thread anchored on a blank template. Returns threadId. */
export async function createFrameThread(template: BlankTemplate): Promise<string> {
  const data = await postJson<{ threadId?: string }>('/api/ai/threads', {
    primaryReferenceType: 'blank_template',
    primaryReferenceId: template.templateId,
    primaryReferenceUrl: template.sourceUrl,
    inferredCanvasSize: { width: template.width, height: template.height }
  })
  if (!data.threadId) throw new Error('Failed to start generation')
  return data.threadId
}

export interface FrameGenerationResult {
  generationId: string
  imageUrl: string
}

/** The messages endpoint validates generationRequestId as a UUID (zod .uuid()). */
function requestUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // RFC-4122-shaped fallback for non-secure contexts.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Sends a prompt to the thread (first generation or iteration). */
export async function sendFramePrompt(
  threadId: string,
  prompt: string
): Promise<FrameGenerationResult> {
  const data = await postJson<{
    generation?: { _id?: string }
    imageUrls?: string[]
  }>(`/api/ai/threads/${encodeURIComponent(threadId)}/messages`, {
    prompt,
    generationRequestId: requestUuid()
  })
  const generationId = data.generation?._id
  const imageUrl = data.imageUrls?.[0]
  if (!generationId || !imageUrl) throw new Error('Generation returned no image')
  return { generationId, imageUrl }
}

/** Saves the chosen chat generation as a private frame (server cuts slots). */
export async function saveChatFrame(input: {
  threadId: string
  generationId: string
  name?: string
}): Promise<CatalogFrame> {
  const data = await postJson<{ frame?: CatalogFrame }>('/api/onboarding/frames', input)
  if (!data.frame) throw new Error('Failed to save frame')
  return data.frame
}

export interface FrameExploreResponse {
  items: CatalogFrame[]
  /** Signed-in creator's own frames (page 1 only). */
  mine: CatalogFrame[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface DraftFrame {
  url: string
  templateId: string
  name: string
}

/**
 * Draws (or returns) the frames designed for this booth from its own spec.
 * Public — no account needed; they become owned frames at create time.
 *
 * NEVER THROWS AND NEVER BLOCKS. Frames are a bonus on top of a working
 * booth: any failure resolves to an empty list and the Strip step falls back
 * to the public catalogue it already renders. A 500 here once put a red error
 * in the console and left the creator staring at a step that looked broken.
 *
 * De-duplicated per draft at module scope, because a remount (or a second
 * tab) firing this twice made the server draw six frames for three and race
 * on the save.
 */
const draftFrameRequests = new Map<string, Promise<DraftFramesResult>>()

export interface DraftFramesResult {
  frames: DraftFrame[]
  /** "drawing" = someone else is already drawing them; try again shortly. */
  status: 'ready' | 'drawing' | 'unavailable'
}

export async function fetchDraftFrames(draftId: string): Promise<DraftFramesResult> {
  const inFlight = draftFrameRequests.get(draftId)
  if (inFlight) return inFlight

  const request = postJson<{ frames?: DraftFrame[]; status?: DraftFramesResult['status'] }>(
    '/api/onboarding/draft-frames',
    { draftId }
  )
    .then((res) => ({
      frames: res.frames ?? [],
      status: res.status ?? (res.frames?.length ? ('ready' as const) : ('unavailable' as const))
    }))
    .catch((err) => {
      console.info('[onboarding] frames unavailable, using the catalogue instead:', err)
      return { frames: [], status: 'unavailable' as const }
    })
    .finally(() => {
      draftFrameRequests.delete(draftId)
    })

  draftFrameRequests.set(draftId, request)
  return request
}

export interface LinkPreview {
  readable: boolean
  reason?: string
  title: string
  description: string
  siteName: string
  images: string[]
}

/** Reads the public preview of a pasted link. Never throws. */
export async function inspectLink(url: string): Promise<LinkPreview> {
  try {
    return await postJson<LinkPreview>('/api/onboarding/inspect', { url })
  } catch {
    return {
      readable: false,
      reason: 'unreachable',
      title: '',
      description: '',
      siteName: '',
      images: []
    }
  }
}

/** Popularity-ranked, searchable, paginated public frames for /new. */
export async function exploreFrames(input: {
  search?: string
  page?: number
  pageSize?: number
  mode?: OnboardingMode
}): Promise<FrameExploreResponse> {
  const params = new URLSearchParams()
  if (input.search) params.set('search', input.search)
  params.set('page', String(input.page ?? 1))
  params.set('pageSize', String(input.pageSize ?? 24))
  if (input.mode) params.set('mode', input.mode)
  const res = await fetch(`${API_BASE()}/api/onboarding/frames?${params.toString()}`, {
    credentials: 'include',
    cache: 'no-cache',
    headers: authHeaders()
  })
  if (!res.ok) throw new Error(`Failed to load frames (${res.status})`)
  return (await res.json()) as FrameExploreResponse
}

export async function fetchOnboardingCatalog(mode: OnboardingMode): Promise<OnboardingCatalog> {
  const res = await fetch(`${API_BASE()}/api/onboarding/catalog?mode=${encodeURIComponent(mode)}`, {
    credentials: 'include',
    cache: 'no-cache',
    headers: authHeaders()
  })
  if (!res.ok) {
    throw new Error(`Failed to load catalog (${res.status})`)
  }
  return (await res.json()) as OnboardingCatalog
}

export interface ShowcaseBooth {
  slug: string
  title: string
  /** Server-side render of the booth's welcome screen, 540x960 portrait. */
  thumbnail: string
  /** Curated "start from this" seed for the composer. May be absent. */
  prompt?: string
  palette: {
    backgroundColor: string
    primaryColor: string
    secondaryColor: string
  }
}

/**
 * Hand-curated live booths for the hero device and starter rail.
 *
 * Never throws and never rejects: /new is the landing page, so a slow or
 * broken showcase degrades to the text starter chips instead of an error
 * state. An empty array is a normal answer (dormant digital_mode flag, nothing
 * curated yet).
 */
export async function fetchShowcaseBooths(limit = 12): Promise<ShowcaseBooth[]> {
  try {
    const res = await fetch(`${API_BASE()}/api/showcase/booths?limit=${limit}`, {
      cache: 'no-cache'
    })
    if (!res.ok) return []
    const data = (await res.json()) as { booths?: ShowcaseBooth[] }
    return Array.isArray(data.booths) ? data.booths.filter((b) => b.slug && b.thumbnail) : []
  } catch {
    return []
  }
}

export interface CreateOnboardingPayload {
  mode: OnboardingMode
  title: string
  slug: string
  draftId?: string
  theme: {
    primaryColor: string
    secondaryColor: string
    backgroundColor: string
  }
  captureMode?: 'standard' | 'frame-based'
  aiEffectId?: string
  frameIds: string[]
  filterIds: string[]
}

/**
 * Creates the project. Returns the created slug, or throws ApiError. A 401
 * carries code AUTH_REQUIRED — the caller opens the sign-in flow and retries
 * once the session appears.
 */
export async function createOnboardingProject(payload: CreateOnboardingPayload): Promise<string> {
  const data = await postJson<{ slug?: string }>('/api/projects/onboarding', payload)
  if (!data.slug) throw new Error('Failed to create project')
  return data.slug
}
